import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { searchContent } from '../../utils/search';
import './SearchModal.css';

const SearchModal = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const history = useHistory();

  // Get all content from Redux
  const contentData = useSelector((state) => state);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchTerm.trim().length >= 2) {
      setIsLoading(true);
      // Debounce search
      const timer = setTimeout(() => {
        const results = searchContent(searchTerm, contentData);
        setSearchResults(results);
        setIsLoading(false);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setSearchResults(null);
      setIsLoading(false);
    }
  }, [searchTerm, contentData]);

  const handleResultClick = (result) => {
    // Build navigation state object
    const navigationState = {
      searchTerm,
      section: result.section,
      scrollTo: result.scrollTo
    };

    // Add specific properties based on result type
    if (result.index !== undefined) {
      navigationState.index = result.index;
    }

    // For rates, pass the PDF title to open the correct PDF
    if (result.pdfTitle) {
      navigationState.pdfTitle = result.pdfTitle;
    }

    // Navigate to the result's route
    history.push(result.route, navigationState);
    
    // Close modal and reset
    setSearchTerm('');
    setSearchResults(null);
    onClose();

    // If there's a scroll target, scroll to it after navigation
    if (result.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(result.scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const handleAboutUs=()=>{
    history.push('/about-us');
    setSearchTerm('');
    setSearchResults(null);
    onClose();
  }

  const handleClear = () => {
    setSearchTerm('');
    setSearchResults(null);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const getResultIcon = (type) => {
    const icons = {
      'Service': '🔧',
      'Contact Information': '📞',
      'Copenhagen Rate': '💰',
      'Billund Rate': '💰',
      'FAQ': '❓',
      'Location': '📍',
      'Page': '📄',
      'Feature': '⭐',
      'Information': 'ℹ️'
    };
    return icons[type] || '📄';
  };

  if (!isOpen) return null;

  return (
    <div className="search-modal-overlay" onClick={onClose}>
      <div className="search-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="search-modal-header">
          <h2 className="search-modal-title">Search</h2>
          <button className="search-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="search-input-container">
          <div className="search-input-wrapper-modal">
            <img src="./search 1.svg" className="search-input-icon" alt="Search" />
            <input
              ref={inputRef}
              type="text"
              className="search-modal-input"
              placeholder="Search services, contacts, rates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {searchTerm && (
              <button className="search-clear-btn" onClick={handleClear}>
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="search-results-container">
          {isLoading && (
            <div className="search-loading">
              <div className="spinner"></div>
              <p>Searching...</p>
            </div>
          )}

          {!isLoading && searchTerm.length < 2 && (
            <div className="search-empty-state">
              <img src="./search 1.svg" className="empty-state-icon" alt="Search" />
              <p className="empty-state-text">Type at least 2 characters to search</p>
              <div className="search-suggestions">
                <p className="suggestions-title">Popular searches:</p>
                <div className="suggestion-chips">
                  <button className="suggestion-chip" onClick={() => setSearchTerm('copenhagen')}>Copenhagen</button>
                  <button className="suggestion-chip" onClick={() => setSearchTerm('billund')}>Billund</button>
                  <button className="suggestion-chip" onClick={() => setSearchTerm('rates')}>Rates</button>
                  <button className="suggestion-chip" onClick={() => handleAboutUs()}>About Us</button>
                </div>
              </div>
            </div>
          )}

          {!isLoading && searchTerm.length >= 2 && !searchResults && (
            <div className="search-no-results">
              <img src="./search 1.svg" className="no-results-icon" alt="No results" />
              <p className="no-results-text">No results found for "{searchTerm}"</p>
              <p className="no-results-subtext">Try different keywords or check your spelling</p>
            </div>
          )}

          {!isLoading && searchResults && searchResults.length > 0 && (
            <div className="search-results-list">
              <p className="results-count">
                Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
              </p>
              {searchResults.map((result, index) => (
                <div
                  key={index}
                  className="search-result-item"
                  onClick={() => handleResultClick(result)}
                >
                  <div className="result-icon">{getResultIcon(result.type)}</div>
                  <div className="result-content">
                    <h3 className="result-title">{result.title}</h3>
                    <div className="result-meta">
                      <span className="result-type">{result.type}</span>
                      {result.location && (
                        <>
                          <span className="result-separator">•</span>
                          <span className="result-location">{result.location}</span>
                        </>
                      )}
                      {result.matchType && (
                        <>
                          <span className="result-separator">•</span>
                          <span className="result-match-type">
                            {result.matchType === 'title' ? 'Title match' : 
                             result.matchType === 'description' ? 'Description match' :
                             result.matchType === 'question' ? 'Question match' :
                             result.matchType === 'answer' ? 'Answer match' : 'Match'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="result-arrow">→</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="search-modal-footer">
          <p className="search-tip">
            <kbd>Esc</kbd> to close
          </p>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
